import { BOX_ITEM_WORD_SPLIT } from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import { I18n } from 'utils/i18n';
import nth from 'utils/nth';
import getPdfjs from 'utils/pdfjs';
import { DOMParser } from '@xmldom/xmldom';
import xpath from 'xpath2';

export const getXPathValue = async (xml, xp) => {
  const doc = new DOMParser().parseFromString(xml);
  return xpath.evaluate(`string(${xp})`, doc).stringValue;
};

export const getPartsFromXML = async (file, xml, format, partDone = () => null) => {
  const doc = new DOMParser().parseFromString(xml);
  const values = format.items.reduce((ret, { name, selector, xpath: xp }) => {
    if (selector === 'user') {
      return ret;
    }
    return { ...ret, [name]: xpath.evaluate(`string(${xp})`, doc).stringValue };
  }, {});
  partDone(values, 1);
  return values;
};

function boxItemViewportBBox(item, viewport, pdfjs) {
  if (viewport?.transform && pdfjs?.Util?.transform) {
    const m = pdfjs.Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(m[2], m[3]) || 10;
    const left = m[4];
    const bottom = m[5];
    const top = bottom - fontHeight;
    // pdfjs `item.width` is already in scale=1 viewport (device) units; the
    // matrix scale is the font size, so multiplying expands every text item's
    // bbox across the page and a tight box catches multiple cells.
    const right = left + (item.width || 0);
    return { left, right, top, bottom };
  }
  const t = item.transform;
  const x = t[4] - viewport.viewBox[0];
  const y = viewport.viewBox[3] - (t[5] + t[3]);
  const w = item.width || 0;
  const h = item.height != null ? item.height : Math.max(Math.abs(t[3]), 8);
  return { left: x, right: x + w, top: y - h, bottom: y };
}

function touchesItem(selBox, itemRect) {
  if (selBox.left > itemRect.right || itemRect.left > selBox.right) return false;
  if (selBox.top > itemRect.bottom || itemRect.top > selBox.bottom) return false;
  const overlapH = Math.min(selBox.bottom, itemRect.bottom) - Math.max(selBox.top, itemRect.top);
  const itemH = itemRect.bottom - itemRect.top;
  if (itemH > 0 && overlapH / itemH <= 0.6) return false;
  return true;
}

function textInBox(textContent, viewport, box, pdfjs, padPx = 1, spans = null) {
  const b = {
    left: Math.min(box.left, box.right) - padPx,
    right: Math.max(box.left, box.right) + padPx,
    top: Math.min(box.top, box.bottom) - padPx,
    bottom: Math.max(box.top, box.bottom) + padPx,
  };
  const items = spans?.length
    ? spans
    : textContent.items
        .filter(ti => ti.str)
        .map(ti => ({ str: ti.str, ...boxItemViewportBBox(ti, viewport, pdfjs) }));
  const hits = [];
  for (const it of items) {
    if (!it.str) continue;
    if (!touchesItem(b, it)) continue;
    hits.push({ left: it.left, midY: (it.top + it.bottom) / 2, str: it.str });
  }
  hits.sort((a, bb) => a.midY - bb.midY || a.left - bb.left);
  return hits.map(h => h.str).join('');
}

const getWordsFromPage = async (pdf, pageNum) => {
  const page = await pdf.getPage(pageNum);
  const viewport = page.getViewport({ scale: 1, rotation: page.rotate });
  const textContent = await page.getTextContent();
  const pdfjs = await getPdfjs();

  const words = textContent.items.reduce((ret, item) => {
    const x = item.transform[4] - viewport.viewBox[0];
    const y = viewport.viewBox[3] - (item.transform[5] + item.transform[3]);
    return {
      ...ret,
      [`${x},${y},${item.width},${item.height}`]: item.str,
    };
  }, []);

  return { words, viewport, textContent, pdfjs };
};

const getPartsFromPDF = async (file, pdf, format, partDone = () => null) => {
  const split = format.type === 'single-pdf';
  const pageNumbers = split ? Array.from({ length: pdf.numPages }, (_, i) => i + 1) : [1];

  const partsPromises = pageNumbers.map(async pageNum => {
    const { words, viewport, textContent, pdfjs } = await getWordsFromPage(pdf, pageNum);
    const parts = format.items.reduce((acc, item) => {
      try {
        let arr;
        if (item.selector === 'user') {
          return acc;
        }
        if (item.selector === 'box') {
          const merged = textInBox(textContent, viewport, item.coords, pdfjs);
          arr = merged ? merged.split(BOX_ITEM_WORD_SPLIT).filter(Boolean) : [];
        } else if (item.selector === 'coords' && item.coords.match(/>/)) {
          const [{ n: x, gt: gtx }, { n: y, gt: gty }] = item.coords
            .split(',')
            .map(n => ({ n: +n.replace('>', ''), gt: n.startsWith('>') }));
          [, arr] = Object.entries(words).find(([key]) => {
            const [wx, wy] = key.split(',').map(n => +n);
            return (gtx ? wx > x : wx === x) && (gty ? wy > y : y === wy);
          });
        } else if (item.selector === 'fileName') {
          const splitRegexp = new RegExp(`[${item.separators}]`);
          arr = file.split(splitRegexp);
        } else {
          [, arr] = Object.entries(words).find(([key]) => key.startsWith(item.coords)) || [];
        }
        arr = typeof arr === 'string' ? arr.trim().split(BOX_ITEM_WORD_SPLIT) : arr;
        let w;
        if (item.selector === 'box') {
          const want = item.position != null ? +item.position : 0;
          const maxIdx = Math.max(0, arr.length - 1);
          const idx = Math.min(want, maxIdx);
          w = nth(arr, idx);
        } else {
          w = nth(arr, item.position);
        }
        const raw = w != null && typeof w === 'string' ? w : '';
        const normalized = raw.replace(/[^ \w-/]/g, '').trim();
        return { ...acc, [item.name]: normalized };
      } catch {
        return { ...acc, [item.name]: '' };
      }
    }, {});

    await partDone(parts, pageNum);
    return parts;
  });

  const results = await Promise.all(split ? partsPromises : [partsPromises[0]]);
  return results;
};

export const getFileName = (partsIn, format) => {
  const parts = { ...partsIn };
  const partMap = format.items.reduce((acc, item) => ({ ...acc, [item.name]: item }), {});
  format.items
    .filter(i => i.selector === 'user')
    .forEach(item => {
      if (!parts[item.name]) {
        const value = I18n.t(`HrBot.fields.${item.name}`);
        parts[item.name] = `{${value}}`;
      }
    });
  let { year, month, day } = parts;
  const sendByValue = parts[format.sendBy] || '';
  let sendBy = sendByValue.replace ? sendByValue.replace(/\//g, '-') : sendByValue;
  const { dni } = parts;
  if (year && year.length < 4) {
    year = `20${year}`;
  }
  sendBy = sendBy ? sendBy.padStart(5, '0') : dni;
  year = partMap.year && year ? `${year}_` : '';
  month = partMap.month && month ? `${month}_` : '';
  day = partMap.day && day ? `${day}_` : '';
  let pref = format.prefix || '';
  if (pref && !pref.endsWith('_')) {
    pref += '_';
  }
  pref += `${year}${month}${day}`;
  return sendBy ? `${pref}${sendBy}.pdf` : '';
};

const getPartFunctions = {
  'multi-pdf': getPartsFromPDF,
  'single-pdf': getPartsFromPDF,
  xml: getPartsFromXML,
};

export const getParts = async (file, fileContents, format, partDone = () => null) => {
  let type = format?.type;
  if (type === 'pdf') {
    type = format.split ? 'single-pdf' : 'multi-pdf';
  }
  const fn = getPartFunctions[type];
  if (typeof fn !== 'function') {
    throw new Error(
      `[HrBot] getParts: unsupported format.type "${format?.type}" (expected single-pdf, multi-pdf, or xml)`,
    );
  }
  return fn(file, fileContents, { ...format, type }, partDone);
};
